import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  APISurvey,
  CustomerSurvey,
  CustomerSurveyQuestion,
  MERCER_DEFAULT_SURVEY,
  MERCER_PROJECT_MANAGEMENT_SURVEY
} from '@coin/shared/customer-survey/util';
import { HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import { environment } from '@coin/shared/core/util-environments';
import { map, Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class CustomerSurveyService {
  private httpClient = inject(HttpClient);
  private loadingService = inject(LoadingService);
  private httpHelpersService = inject(HttpHelpersService);

  public getSurvey(id: string, withLoadingScreen = true): Observable<CustomerSurvey> {
    return this.httpClient
      .get<APISurvey>(`${environment.api.storageService}/v1/position-evaluation/surveys/${id}`)
      .pipe(map(mapFromApiSurvey), o$ => (withLoadingScreen ? this.loadingService.withLoadingScreen(o$) : o$));
  }

  public createSurvey(survey: CustomerSurvey): Observable<CustomerSurvey> {
    this.loadingService.present();
    return this.httpClient
      .post<APISurvey>(`${environment.api.storageService}/v1/position-evaluation/surveys`, mapToApiSurvey(survey))
      .pipe(
        map(mapFromApiSurvey),
        this.httpHelpersService.handleError('Cannot create survey'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public updateSurvey(survey: CustomerSurvey): Observable<CustomerSurvey> {
    if (!survey) {
      return this.httpHelpersService.throwError('survey.errors.update');
    }

    this.loadingService.present();
    return this.httpClient
      .patch<APISurvey>(`${environment.api.storageService}/v1/position-evaluation/surveys`, mapToApiSurvey(survey))
      .pipe(
        map(mapFromApiSurvey),
        this.httpHelpersService.handleError('Cannot save survey results'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public deleteSurvey(id: string): Observable<void> {
    this.loadingService.present();
    return this.httpClient.delete<void>(`${environment.api.storageService}/v1/position-evaluation/surveys/${id}`).pipe(
      this.httpHelpersService.handleError('Cannot delete survey'),
      finalize(() => this.loadingService.dismiss())
    );
  }
}

function mapToApiSurvey({
  id,
  surveyType,
  createdAt,
  createdByGid,
  modifiedByGid,
  modifiedAt,
  primaryKey,
  questions
}: CustomerSurvey): APISurvey {
  return {
    id,
    primaryKey,
    surveyType,
    createdAt,
    createdByGid,
    modifiedAt,
    modifiedByGid,
    answerIds: questions.map(q => q.selectedAnswerId).filter(Boolean)
  };
}

function mapFromApiSurvey(survey: APISurvey): CustomerSurvey {
  if (!survey) return survey as CustomerSurvey;
  if (!('answerIds' in survey)) return survey as CustomerSurvey; // old surveys from before model was trimmed down

  const surveyTemplate = survey.surveyType === 'Default' ? MERCER_DEFAULT_SURVEY : MERCER_PROJECT_MANAGEMENT_SURVEY;

  const questions: CustomerSurveyQuestion[] = surveyTemplate.questions.map(question => {
    const answerIdsFromQuestion = question.answers.map(a => a.id);
    const selectedAnswerId = survey.answerIds.find(answerId => answerIdsFromQuestion.includes(answerId));
    return {
      ...question,
      selectedAnswerId,
      isActive: !!selectedAnswerId,
      answers: question.answers.map(answer => ({ ...answer, isSelected: selectedAnswerId === answer.id }))
    };
  });

  return { ...survey, questions, results: surveyTemplate.results };
}
